import { z } from 'zod'

/**
 * QR特典（QR Reward）ドメインのスキーマ。
 * バックエンド #3262（`QrRewardController`）の API 契約に対応する。
 */

export const QrRewardPrizeSelectionTypeEnum = z.enum(['SINGLE', 'LOTTERY'])
export type QrRewardPrizeSelectionType = z.infer<typeof QrRewardPrizeSelectionTypeEnum>

/** 賞品種別（既存 Reward の prize.type と同一体系） */
export const QrRewardPrizeTypeEnum = z.enum(['ITEM', 'DLC', 'AR_PHOTO', 'COUPON', 'GIFT_CODE', 'GIFTEE'])
export type QrRewardPrizeType = z.infer<typeof QrRewardPrizeTypeEnum>

/** 獲得結果ステータス（acquired=獲得済み / lost=抽選ハズレ） */
export const QrRewardAcquireStatusEnum = z.enum(['acquired', 'lost'])
export type QrRewardAcquireStatus = z.infer<typeof QrRewardAcquireStatusEnum>

// ─────────────────────────────────────────────
// QR特典情報
// ─────────────────────────────────────────────
export const QrRewardDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  /** 認証が必要か（true=認証ON） */
  authRequired: z.boolean(),
  prizeSelectionType: QrRewardPrizeSelectionTypeEnum,
  /** CloudFront URL */
  image: z.string().nullable(),
  /** QR特典説明（HTML） */
  description: z.string().nullable(),
  url: z.string().nullable(),
})
export type QrRewardData = z.infer<typeof QrRewardDataSchema>

/** QR特典で獲得した賞品情報 */
export const QrRewardPrizeDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: QrRewardPrizeTypeEnum,
  /** CloudFront URL */
  image: z.string().nullable(),
  description: z.string().nullable(),
  /** 賞品URL（GIFT_CODE時は払い出したコードURL） */
  url: z.string().nullable(),
  /** GIFT_CODE時に払い出したコード */
  code: z.string().nullable(),
  /** DLCコンテンツの期限付き署名URL（#3352）。type==='DLC' かつ content_file 設定時のみ非null */
  contentFile: z.string().nullable(),
})
export type QrRewardPrizeData = z.infer<typeof QrRewardPrizeDataSchema>

// ─────────────────────────────────────────────
// GET /api/v2/qr-rewards/{id}
// ─────────────────────────────────────────────
export const QrRewardDetailResponseSchema = z.object({
  qrReward: QrRewardDataSchema,
  /** このユーザー/デバイスが既に獲得済みか */
  acquired: z.boolean(),
  /** 獲得済みの場合の賞品 */
  prize: QrRewardPrizeDataSchema.nullable(),
})
export type QrRewardDetailResponse = z.infer<typeof QrRewardDetailResponseSchema>

// ─────────────────────────────────────────────
// POST /api/v2/qr-rewards/{id}/acquire
// ─────────────────────────────────────────────
export const QrRewardAcquireResponseSchema = z.object({
  status: QrRewardAcquireStatusEnum,
  /** true = 既に獲得済みで再取得された（新規獲得ではない） */
  alreadyAcquired: z.boolean(),
  qrReward: QrRewardDataSchema,
  prize: QrRewardPrizeDataSchema.nullable(),
})
export type QrRewardAcquireResponse = z.infer<typeof QrRewardAcquireResponseSchema>
